// Instanced CPU particle system: additive sparks + gravity debris. Two draw calls total.
// Per-particle fade is done by shrinking scale (opacity is per-material on InstancedMesh).

import * as THREE from 'three';

const MAX_SPARKS = 512;
const MAX_DEBRIS = 256;

interface Particle {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  ttl: number;
  maxTtl: number;
  size: number;
  gravity: number;
  color: THREE.Color;
  spin: number;
}

export interface BurstOpts {
  count: number;
  colors: number[];
  /** min/max launch speed (u/s) */
  speed: [number, number];
  ttl: [number, number];
  size: [number, number];
  gravity: number;
  /** 0 = spherical, 1 = strongly upward */
  upBias: number;
}

class Pool {
  readonly mesh: THREE.InstancedMesh;
  readonly live: Particle[] = [];
  private readonly max: number;
  private readonly tmpMat = new THREE.Matrix4();
  private readonly tmpQuat = new THREE.Quaternion();
  private readonly tmpEuler = new THREE.Euler();
  private readonly tmpPos = new THREE.Vector3();
  private readonly tmpScale = new THREE.Vector3();
  private density = 1;

  constructor(scene: THREE.Scene, mat: THREE.Material, max: number) {
    this.max = max;
    this.mesh = new THREE.InstancedMesh(new THREE.TetrahedronGeometry(0.09), mat, max);
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.count = 0;
    this.mesh.frustumCulled = false;
    scene.add(this.mesh);
  }

  setDensity(density: number): void {
    this.density = Math.max(0, density);
  }

  burst(x: number, y: number, z: number, o: BurstOpts): void {
    const count = Math.round(o.count * this.density);
    for (let i = 0; i < count; i++) {
      if (this.live.length >= this.max) return;
      // random direction, biased upward
      const a = Math.random() * Math.PI * 2;
      const up = o.upBias + (1 - o.upBias) * (Math.random() * 2 - 1);
      const horiz = Math.sqrt(Math.max(0, 1 - up * up));
      const speed = o.speed[0] + Math.random() * (o.speed[1] - o.speed[0]);
      const ttl = o.ttl[0] + Math.random() * (o.ttl[1] - o.ttl[0]);
      this.live.push({
        x,
        y,
        z,
        vx: Math.cos(a) * horiz * speed,
        vy: Math.abs(up) * speed * (o.upBias > 0 ? 1 : Math.sign(up) || 1),
        vz: Math.sin(a) * horiz * speed,
        ttl,
        maxTtl: ttl,
        size: o.size[0] + Math.random() * (o.size[1] - o.size[0]),
        gravity: o.gravity,
        color: new THREE.Color(o.colors[(Math.random() * o.colors.length) | 0]),
        spin: Math.random() * Math.PI * 2,
      });
    }
  }

  update(dt: number): void {
    const live = this.live;
    for (let i = live.length - 1; i >= 0; i--) {
      const p = live[i];
      p.ttl -= dt;
      if (p.ttl <= 0) {
        live[i] = live[live.length - 1];
        live.pop();
        continue;
      }
      p.vy += p.gravity * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.z += p.vz * dt;
      if (p.y < 0.05 && p.gravity !== 0) {
        p.y = 0.05;
        p.vy = Math.abs(p.vy) * 0.35; // bounce
        p.vx *= 0.7;
        p.vz *= 0.7;
      }
    }
    for (let i = 0; i < live.length; i++) {
      const p = live[i];
      const life = p.ttl / p.maxTtl;
      const s = p.size * life;
      this.tmpPos.set(p.x, p.y, p.z);
      this.tmpEuler.set(p.spin, p.spin * 1.7, p.spin * 0.6);
      this.tmpQuat.setFromEuler(this.tmpEuler);
      this.tmpScale.set(s, s, s);
      this.tmpMat.compose(this.tmpPos, this.tmpQuat, this.tmpScale);
      this.mesh.setMatrixAt(i, this.tmpMat);
      this.mesh.setColorAt(i, p.color);
    }
    this.mesh.count = live.length;
    this.mesh.instanceMatrix.needsUpdate = true;
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
  }
}

export class ParticleSystem {
  private sparks: Pool;
  private debris: Pool;

  constructor(scene: THREE.Scene) {
    this.sparks = new Pool(
      scene,
      new THREE.MeshBasicMaterial({
        blending: THREE.AdditiveBlending,
        transparent: true,
        depthWrite: false,
      }),
      MAX_SPARKS,
    );
    this.sparks.mesh.renderOrder = 8;
    this.debris = new Pool(scene, new THREE.MeshStandardMaterial({ roughness: 0.9, flatShading: true }), MAX_DEBRIS);
    this.debris.mesh.castShadow = false;
  }

  setQuality(quality: 'low' | 'medium' | 'high' | 'ultra', reducedMotion: boolean): void {
    const density = quality === 'low' ? 0.35 : quality === 'medium' ? 0.65 : quality === 'ultra' ? 1.25 : 1;
    const motionScale = reducedMotion ? 0.45 : 1;
    this.sparks.setDensity(density * motionScale);
    this.debris.setDensity(density * motionScale);
  }

  /** warm impact sparks */
  hit(x: number, z: number): void {
    this.sparks.burst(x, 1.2, z, {
      count: 10,
      colors: [0xffd27f, 0xffb04a, 0xfff0c0],
      speed: [3, 6.5],
      ttl: [0.2, 0.45],
      size: [1.4, 2.6],
      gravity: -12,
      upBias: 0.55,
    });
  }

  /** ground dust kicked up by giant footfalls and slam impacts */
  dust(x: number, z: number, scale = 1, colors: number[] = [0x8a7f6a, 0x6e6454, 0x9a8d76]): void {
    this.debris.burst(x, 0.15, z, {
      count: Math.round(7 * scale),
      colors,
      speed: [0.8, 2.4 * scale],
      ttl: [0.4, 0.9],
      size: [2.6 * scale, 5 * scale],
      gravity: -3,
      upBias: 0.4,
    });
  }

  /** death debris (owner-tinted chunks + grey); y for kills that happen up in a giant's fist */
  death(x: number, z: number, ownerColor: number, big = false, y = 0.9): void {
    this.debris.burst(x, y, z, {
      count: big ? 22 : 11,
      colors: [0x8a8f96, 0x6b7078, ownerColor],
      speed: big ? [3, 8] : [2, 5.5],
      ttl: [0.5, 1.1],
      size: big ? [3, 6] : [2, 4],
      gravity: -16,
      upBias: 0.6,
    });
  }

  /** Sproutling casualty: colorful petals and seeds ride over a dense, low pollen puff. */
  sproutlingBurst(x: number, z: number, big = false): void {
    this.sparks.burst(x, 0.62, z, {
      count: big ? 34 : 18,
      colors: [0xf06a79, 0xffcf55, 0x79a9ef, 0xd987d6, 0xffffff],
      speed: big ? [2.5, 7] : [1.8, 5],
      ttl: [0.45, 1.0],
      size: [1.8, 3.8],
      gravity: -7,
      upBias: 0.62,
    });
    this.debris.burst(x, 0.28, z, {
      count: big ? 28 : 15,
      colors: [0xe6d987, 0xc9c36a, 0xa8c875, 0x8fa65a],
      speed: [0.5, big ? 3.2 : 2.2],
      ttl: [0.7, 1.45],
      size: [3.4, big ? 7.5 : 5.8],
      gravity: -1.2,
      upBias: 0.42,
    });
  }

  /** Distinct fungal release: dark heavy motes for harm, warm luminous motes for renewal. */
  fungalBurst(x: number, z: number, healing: boolean): void {
    this.debris.burst(x, 0.55, z, {
      count: 24,
      colors: healing ? [0xbbe883, 0x8fcf68, 0xe6f5b2] : [0x4d355c, 0x6c4b76, 0x51463f],
      speed: healing ? [0.6, 2.3] : [0.35, 1.65],
      ttl: healing ? [0.8, 1.55] : [1.0, 1.9],
      size: healing ? [2.8, 5.5] : [4.5, 8.5],
      gravity: healing ? -0.6 : -0.2,
      upBias: healing ? 0.58 : 0.34,
    });
    if (healing) {
      this.sparks.burst(x, 0.4, z, {
        count: 16,
        colors: [0xdff7a0, 0xa9e878, 0xffffff],
        speed: [0.5, 2.5],
        ttl: [0.55, 1.15],
        size: [1.4, 2.8],
        gravity: -0.5,
        upBias: 0.65,
      });
    }
  }

  solarBloom(x: number, z: number): void {
    this.sparks.burst(x, 1.2, z, {
      count: 38,
      colors: [0xffdf72, 0xf3c94f, 0xfff3bd, 0xffffff],
      speed: [2, 8],
      ttl: [0.5, 1.1],
      size: [2, 4.8],
      gravity: -1.5,
      upBias: 0.55,
    });
  }

  /** building collapse: stone chunks */
  collapse(x: number, z: number): void {
    this.debris.burst(x, 1.5, z, {
      count: 26,
      colors: [0x8d8578, 0x6b655c, 0x5d554a],
      speed: [3, 9],
      ttl: [0.6, 1.3],
      size: [4, 8],
      gravity: -18,
      upBias: 0.65,
    });
  }

  /** colored magic/ability burst */
  burst(x: number, z: number, color: number, big = false): void {
    this.sparks.burst(x, 1.0, z, {
      count: big ? 26 : 14,
      colors: [color, 0xffffff],
      speed: big ? [4, 9] : [2.5, 6],
      ttl: [0.3, 0.7],
      size: big ? [2.5, 4.5] : [1.5, 3],
      gravity: -4,
      upBias: 0.35,
    });
  }

  /** Boss-specific arrival debris: each boss gets a distinct material/color story. */
  bossSummon(defId: string, x: number, z: number): void {
    const isThornback = defId === 'boss-vw-thornback';
    const isGroveback = defId === 'boss-vw-ancient-groveback';
    const isTitan = defId === 'boss-vw-worldroot-titan';
    const profiles: Record<string, { colors: number[]; count: number; spark: number; upBias: number }> = {
      'boss-ic-arcturus': { colors: [0x8794a8, 0xd8b04a, 0xffffff], count: 28, spark: 0xffd76e, upBias: 0.6 },
      'boss-ic-iron-saint': { colors: [0xced7e6, 0xffe7a0, 0x8fb8ff], count: 38, spark: 0xffe8a4, upBias: 0.82 },
      'boss-ic-crown-colossus': { colors: [0x6b655c, 0xd8b04a, 0xfff0b0], count: 54, spark: 0xffd76e, upBias: 0.55 },
      'boss-ah-veyr': { colors: [0x38263f, 0x9d65c4, 0xe8b5ff], count: 30, spark: 0xc98be8, upBias: 0.72 },
      'boss-ah-gravebound-colossus': { colors: [0x51463f, 0x8a8578, 0xb8a094], count: 52, spark: 0xc8b8a8, upBias: 0.5 },
      'boss-ah-hollow-king': { colors: [0x3b2730, 0x9b4a62, 0xff8f8f], count: 34, spark: 0xff6c6c, upBias: 0.68 },
      'boss-ad-xyra': { colors: [0x245a78, 0x52d9ff, 0xd1f6ff], count: 32, spark: 0x73e8ff, upBias: 0.9 },
      'boss-ad-arbiter': { colors: [0x42568a, 0x8ca8ff, 0xe8efff], count: 42, spark: 0x9dbbff, upBias: 0.85 },
      'boss-ad-war-engine': { colors: [0x53616e, 0x9aa8b8, 0xffb35c], count: 58, spark: 0xffb35c, upBias: 0.48 },
    };
    const profile = profiles[defId] ?? {
      colors: isThornback ? [0x6b4a2e, 0x8a9a5a, 0x9aca5a] : isGroveback ? [0x5c4328, 0x6f8f45, 0xd6a84f] : [0x4c4638, 0x6f8f45, 0x9fe06a],
      count: isGroveback ? 42 : isTitan ? 56 : 32,
      spark: isThornback ? 0x9aca5a : isGroveback ? 0xd6a84f : 0x8fe05a,
      upBias: isGroveback ? 0.72 : 0.58,
    };
    this.debris.burst(x, 0.35, z, {
      count: profile.count,
      colors: profile.colors,
      speed: [3.2, isGroveback ? 8.5 : 10.5],
      ttl: [0.8, 1.7],
      size: [3, isGroveback ? 8 : 7],
      gravity: -10,
      upBias: profile.upBias,
    });
    this.sparks.burst(x, isGroveback ? 0.8 : 1.1, z, {
      count: isGroveback ? 36 : 26,
      colors: [profile.spark, ...profile.colors.slice(0, 2)],
      speed: [2.5, isGroveback ? 6.5 : 8],
      ttl: [0.45, 1.15],
      size: [1.5, 4],
      gravity: -2,
      upBias: isGroveback ? 0.9 : 0.62,
    });
  }

  /** ballista muzzle flash */
  muzzle(x: number, y: number, z: number): void {
    this.sparks.burst(x, y, z, {
      count: 6,
      colors: [0xfff0c0, 0xffd27f],
      speed: [1.5, 3.5],
      ttl: [0.12, 0.25],
      size: [1.2, 2.2],
      gravity: 0,
      upBias: 0.2,
    });
  }

  update(dt: number): void {
    this.sparks.update(dt);
    this.debris.update(dt);
  }
}
